import { usePage, useRemember } from "@inertiajs/react";
import { useEffect, useMemo } from "react";
import { DefaultPageType } from "types/DefaultPageType";
import { createStore, RootState } from "./index";
import { EnhancedStore } from "@reduxjs/toolkit";

interface SavedState {
  generatedAt: Date;
  rootState: RootState;
}

// Merge the states
function generateState({
  generatedAt,
  rootState,
  rememberedState,
}: {
  generatedAt: Date;
  rootState: RootState;
  rememberedState: SavedState;
}): RootState {
  const rememberedDate = rememberedState.generatedAt;
  const newRootState = generatedAt >= rememberedDate;
  const canonicalState = newRootState ? rootState : rememberedState.rootState;

  
  const currentUser = {
    ...canonicalState.currentUser,
    session: rootState.currentUser.session,
    followedUsers: {
      ...rememberedState.rootState.currentUser?.followedUsers,
      ...canonicalState.currentUser.followedUsers,
    }
  }
  const bookButton = {
    ...canonicalState.bookButton,
    ...rememberedState.rootState.bookButton,
  }

  return {
    currentUser,
    bookButton,
    books: canonicalState.books,
    letterbooks: canonicalState.letterbooks,
    search: canonicalState.search,
    ui: rememberedState.rootState.ui,
  }
}
export function generateStore():EnhancedStore<RootState> {
  const { generatedAt, rootState } = usePage<DefaultPageType>().props;

  // Use `useRemember` for persisted state
  const [rememberedState, setRememberedState] = useRemember<SavedState>({
    generatedAt: new Date(generatedAt * 1000),
    rootState: rootState,
  });

  // Create Redux store with the initial state
  // This overrides changes to the initial state with remembered state with current state
  const store = useMemo(() => {
    const newState = generateState({
      generatedAt: new Date(generatedAt * 1000),
      rootState,
      rememberedState
    });
    return createStore(newState);
  }, [generatedAt]);

  // Subscribe to Redux store updates to sync with remembered state
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const currentState = store.getState();
      // Update remembered state
      setRememberedState({
        rootState: currentState,
        generatedAt: new Date()
      }); 
    });

    return () => {
      unsubscribe(); // Cleanup subscription on unmount
    };
  }, [store, setRememberedState]);

  return store;
}
